import * as React from 'react';
import { Form } from '@revfluence/fresh';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from 'class-variance-authority';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import moment, { Moment } from 'moment';
import { FormAsyncAction, IShopifyFormElementProps, OfferFormAsyncActions } from '../../../../../../../types';

interface IProps extends IShopifyFormElementProps<'endDate'> {
  startDate: Moment;
  setOfferEndDate: React.Dispatch<React.SetStateAction<boolean>>;
  handleFormAsyncActions: (action: FormAsyncAction) => void;
  value: Moment;
}

export const RefreshOfferEndDate: React.FC<Readonly<IProps>> = React.memo(({
  disabled,
  name,
  startDate,
  handleFormAsyncActions,
  value
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Moment | null>(value);
  const [currentMonth, setCurrentMonth] = React.useState<Date>(selectedDate?.toDate() || new Date());
  const [yearSelectOpen, setYearSelectOpen] = React.useState(false);

  const years = React.useMemo(() => {
    const currentYear = moment().year();
    return Array.from({ length: 10 }, (_, i) => currentYear + i);
  }, []);

  const handleYearChange = (year: string) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(parseInt(year, 10));
    setCurrentMonth(newDate);

    if (selectedDate) {
      const newSelectedDate = selectedDate.clone().year(parseInt(year, 10));
      setSelectedDate(newSelectedDate);

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: name,
          value: newSelectedDate,
        },
      });

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: 'isEndDateEnable',
          value: !!newSelectedDate,
        },
      });
    }

    setYearSelectOpen(false);
  };

  const goToPreviousYear = () => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(newDate.getFullYear() - 1);
    setCurrentMonth(newDate);

    if (selectedDate) {
      const newSelectedDate = selectedDate.clone().year(newDate.getFullYear());
      setSelectedDate(newSelectedDate);

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: name,
          value: newSelectedDate,
        },
      });

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: 'isEndDateEnable',
          value: !!newSelectedDate,
        },
      });
    }
  };

  const goToNextYear = () => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(newDate.getFullYear() + 1);
    setCurrentMonth(newDate);

    if (selectedDate) {
      const newSelectedDate = selectedDate.clone().year(newDate.getFullYear());
      setSelectedDate(newSelectedDate);

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: name,
          value: newSelectedDate,
        },
      });

      handleFormAsyncActions({
        action: OfferFormAsyncActions.UPDATE_FIELD,
        payload: {
          key: 'isEndDateEnable',
          value: !!newSelectedDate,
        },
      });
    }
  };

  const handleDateChange = (date: Date | undefined) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }
    const now = moment();
    const momentDate = moment.utc(moment(date).set({
      hour: now.hour(),
      minute: now.minute(),
      second: now.second(),
    }));
    setSelectedDate(momentDate);
    setCurrentMonth(date);

    handleFormAsyncActions({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: {
        key: name,
        value: momentDate,
      },
    });
    handleFormAsyncActions({
      action: OfferFormAsyncActions.UPDATE_FIELD,
      payload: {
        key: 'isEndDateEnable',
        value: !!momentDate,
      },
    });
  };

  return (
    <Form.Item name={name}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cx(
              'w-[250px] justify-start text-left font-normal',
              !selectedDate && 'text-muted-foreground'
            )}
            disabled={disabled}
          >
            {selectedDate ? selectedDate.format('MMM D, YYYY') : 'Pick a date'}
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          {/* Year navigation controls */}
          <div className="flex items-center justify-between p-2 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPreviousYear}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous Year</span>
            </Button>

            <Select
              value={currentMonth.getFullYear().toString()}
              onValueChange={handleYearChange}
              open={yearSelectOpen}
              onOpenChange={setYearSelectOpen}
            >
              <SelectTrigger className="h-7 w-[85px] text-sm">
                <SelectValue placeholder={currentMonth.getFullYear().toString()} />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              onClick={goToNextYear}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next Year</span>
            </Button>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate?.toDate()}
            onSelect={handleDateChange}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            disabled={(date) => moment(date).isBefore(startDate, 'day')}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </Form.Item>
  );
});

RefreshOfferEndDate.displayName = 'RefreshOfferEndDate';
